import type { FunctionInterpolation, Interpolation } from "@emotion/react";

export type CSSAngle = `${number}${"deg" | "grad" | "rad" | "turn"}`;

const unitToDegrees = (angle: CSSAngle): number => {
  const num = parseInt(angle.replaceAll(/\D/g, ""));
  const unit = angle.replaceAll(/\d/g, "");
  if (unit === "deg") return num;
  if (unit === "grad") return (num / 100) * 90;
  if (unit === "rad") return (num * 180) / Math.PI;
  if (unit === "turn") return num * 360;
  return 0;
};

export const gradientComposer = (angle: CSSAngle) =>
  `linear-gradient(${angle}, rgba(0, 0, 0, 0) 49%, rgba(0, 0, 0, 1) 50%)`;

export const maskSizeComposer = (angle: CSSAngle) => {
  const inDegrees = unitToDegrees(angle);
  if (
    inDegrees < 45 ||
    inDegrees > 315 ||
    (inDegrees > 135 && inDegrees < 225)
  ) {
    return "100% 300%";
  }

  return "300% 100%";
};

export const maskPositionComposer = (
  angle: CSSAngle,
  position: "start" | "end",
) => {
  const inDegrees = unitToDegrees(angle);
  if (inDegrees < 45 || inDegrees > 315) {
    if (position === "start") {
      return "center bottom";
    }
    return "center top";
  }
  if (inDegrees > 135 && inDegrees < 225) {
    if (position === "start") {
      return "center top";
    }
    return "center bottom";
  }
  if (inDegrees < 180) {
    if (position === "start") {
      return "left center";
    }
    return "right center";
  }
  if (position === "start") {
    return "right center";
  }
  return "left center";
};

type MaskProps = { maskImage: string; maskPosition: string; maskSize: string };
export const wipe: {
  base: Interpolation;
  entering: FunctionInterpolation<MaskProps>;
  entered: FunctionInterpolation<MaskProps>;
} = {
  base: {
    maskImage: "none",
    maskMode: "alpha",
    maskOrigin: "border-box",
    transitionProperty: "mask-position",
    willChange: "mask-position",
  },
  entering: (props: {
    maskImage: string;
    maskSize: string;
    maskPosition: string;
  }) => ({
    maskImage: props.maskImage,
    maskPosition: props.maskPosition,
    maskSize: props.maskSize,
  }),
  entered: (props: {
    maskImage: string;
    maskSize: string;
    maskPosition: string;
  }) => ({
    maskImage: props.maskImage,
    maskPosition: props.maskPosition,
    maskSize: props.maskSize,
  }),
};
